import * as React from 'react';

import { getApplicationDomain } from '@gitbook/env';
import { LoadingPanel } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useHideSplashscreen } from '../../Components';
import { useHistory } from '../../navigation';
import {
    type FirebaseActionURLParams,
    useCurrentUser,
    useFirebaseActionURLParams,
    useIsRedirectedForAuth,
} from '../../state';
import { LoginScreen } from './LoginScreen';
import { StepApplyAuthRedirect } from './StepApplyAuthRedirect';
import { StepApplyPasswordResetLink } from './StepApplyPasswordResetLink';
import { StepApplyRecoverEmailLink } from './StepApplyRecoverEmailLink';
import { StepApplySignInLink } from './StepApplySignInLink';
import { StepApplyVerifyAndChangeEmailLink } from './StepApplyVerifyAndChangeEmailLink';
import { StepApplyVerifyEmailLink } from './StepApplyVerifyEmailLink';
import { StepRedirectToContinueUrl } from './StepRedirectToContinueUrl';
import { StepRedirectToGitHubAuth } from './StepRedirectToGitHubAuth';

/**
 * Show login screen if the user is unauthenticated.
 */
export function LoginScreenWrapper(props: { children: React.ReactNode }): React.ReactElement {
    const firebaseUser = useCurrentUser();
    const firebaseAction = useFirebaseActionURLParams();

    const isRedirecting = useIsRedirectingToContinueUrl(firebaseAction?.continueUrl);
    const shouldRedirectToAuth = useIsStartingRedirectFlow();
    const isAuthenticatingWithRedirect = useIsRedirectedForAuth();

    if (isAuthenticatingWithRedirect) {
        return <StepApplyAuthRedirect onDone={isAuthenticatingWithRedirect} />;
    }

    if (isRedirecting) {
        return <StepRedirectToContinueUrl />;
    }

    if (shouldRedirectToAuth && !firebaseUser) {
        return <StepRedirectToGitHubAuth />;
    }

    // When the user is logging-in, we let the login screen handles the login logic
    // ex: it allows to login on desktop (redirect done by LoginScreen) when the user is already logged-in
    if (firebaseAction) {
        return <FirebaseActionsWrapper firebaseAction={firebaseAction} />;
    }

    if (!firebaseUser) {
        return <LoginScreen />;
    }

    return <React.Suspense fallback={<LoadingPanel />}>{props.children}</React.Suspense>;
}

/**
 * Handle the Firebase action URL parameters.
 */
function FirebaseActionsWrapper(props: {
    firebaseAction: FirebaseActionURLParams;
}): React.ReactElement {
    const { firebaseAction } = props;
    useHideSplashscreen(0);

    switch (firebaseAction.mode) {
        case 'resetPassword':
            return <StepApplyPasswordResetLink action={firebaseAction} />;
        case 'signIn':
            return <StepApplySignInLink />;
        case 'verifyEmail':
            return <StepApplyVerifyEmailLink action={firebaseAction} />;
        case 'recoverEmail':
            return <StepApplyRecoverEmailLink action={firebaseAction} />;
        case 'verifyAndChangeEmail':
            return <StepApplyVerifyAndChangeEmailLink action={firebaseAction} />;
        default:
            assertNever(firebaseAction.mode);
    }
}

/**
 * The continueUrl is the original url where the user action took place.
 * Our authentication can happen on any url if it's on the correct domain.
 */
function useIsRedirectingToContinueUrl(continueUrl: string | undefined): boolean {
    const history = useHistory();
    const currentUrl = window.location.href;

    const redirect = React.useMemo(() => {
        const parsedCurrentUrl = new URL(currentUrl);
        const parsedContinueUrl = continueUrl ? new URL(continueUrl) : null;

        const redirectTo =
            parsedContinueUrl &&
            // For security reasons, we only allow redirecting to a continue-url we own.
            // For development, 127.0.0.1 is accepted.
            parsedContinueUrl.host === getApplicationDomain()
                ? parsedContinueUrl
                : parsedCurrentUrl;

        const isSameHost = parsedCurrentUrl.host === redirectTo.host;
        const isSamePath = parsedCurrentUrl.pathname === redirectTo.pathname;

        if (!isSameHost) {
            // Full redirect because we are changing host
            redirectTo.search = parsedCurrentUrl.search;
            return { url: redirectTo.toString() };
        }
        if (!isSamePath) {
            // Replace path when redirecting on same host
            return { path: `${redirectTo.pathname}${parsedCurrentUrl.search}` };
        }

        return null;
    }, [continueUrl, currentUrl]);

    React.useEffect(() => {
        if (redirect?.url) {
            window.location.href = redirect.url;
        } else if (redirect?.path) {
            history.replace({ pathname: redirect.path });
        }
    }, [history, redirect]);

    return !!redirect;
}

/**
 * Return true if we should start a redirect flow.
 * It's done by passing ?auth=github into the url.
 */
function useIsStartingRedirectFlow(): boolean {
    return React.useMemo(() => {
        const url = new URL(window.location.href);
        return url.searchParams.get('auth') === 'github';
    }, []);
}
